const KEYS = {
  CLAIM_TOKENS: "wizard_claim_tokens",
  WIZARD_DATA: "wizard_draw_data",
  WIZARD_STEP: "wizard_current_step",
  ACTIVE_DRAW_ID: "wizard_active_draw_id",
} as const;

function safeRead(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeWrite(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
  }
}

function safeRemove(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
  }
}

export const CLAIM_TOKEN_TTL_MS = 48 * 60 * 60 * 1000;

export interface ClaimTokenEntry {
  token: string;
  expiresAt: number;
}

type ClaimTokenMap = Record<string, ClaimTokenEntry>;

function readClaimTokenMap(): ClaimTokenMap {
  const raw = safeRead(KEYS.CLAIM_TOKENS);
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object") {
      return parsed as ClaimTokenMap;
    }
    return {};
  } catch {
    return {};
  }
}

function writeClaimTokenMap(map: ClaimTokenMap): void {
  safeWrite(KEYS.CLAIM_TOKENS, JSON.stringify(map));
}

export function setClaimToken(
  drawId: string,
  token: string,
  expiresAt: number = Date.now() + CLAIM_TOKEN_TTL_MS
): void {
  const map = readClaimTokenMap();
  map[drawId] = { token, expiresAt };
  writeClaimTokenMap(map);
}

export function getClaimToken(drawId: string): string | null {
  const map = readClaimTokenMap();
  const entry = map[drawId];
  if (!entry) return null;
  if (entry.expiresAt <= Date.now()) {
    delete map[drawId];
    writeClaimTokenMap(map);
    return null;
  }
  return entry.token;
}

export function clearClaimToken(drawId: string): void {
  const map = readClaimTokenMap();
  if (drawId in map) {
    delete map[drawId];
    writeClaimTokenMap(map);
  }
}

export function clearAllClaimTokens(): void {
  safeRemove(KEYS.CLAIM_TOKENS);
}

export function getStoredDrawIds(): string[] {
  const map = readClaimTokenMap();
  const now = Date.now();
  return Object.entries(map)
    .filter(([, entry]) => entry.expiresAt > now)
    .sort(([, a], [, b]) => b.expiresAt - a.expiresAt)
    .map(([drawId]) => drawId);
}

export interface WizardSession {
  data: Record<string, unknown>;
  step: number;
}

export function setWizardSession(data: unknown, step: number): void {
  safeWrite(KEYS.WIZARD_DATA, JSON.stringify(data));
  safeWrite(KEYS.WIZARD_STEP, String(step));
}

export function hasWizardSession(): boolean {
  return safeRead(KEYS.WIZARD_DATA) !== null;
}

export function getWizardSession(): WizardSession | null {
  const rawData = safeRead(KEYS.WIZARD_DATA);
  if (!rawData) return null;

  let data: Record<string, unknown>;
  try {
    const parsed = JSON.parse(rawData);
    if (!parsed || typeof parsed !== "object") {
      clearWizardSession();
      return null;
    }
    data = parsed as Record<string, unknown>;
  } catch {
    clearWizardSession();
    return null;
  }

  const rawStep = safeRead(KEYS.WIZARD_STEP);
  const step = rawStep ? parseInt(rawStep, 10) : 0;
  return { data, step: Number.isNaN(step) ? 0 : step };
}

export function clearWizardSession(): void {
  safeRemove(KEYS.WIZARD_DATA);
  safeRemove(KEYS.WIZARD_STEP);
}

export function setActiveDrawId(id: string): void {
  safeWrite(KEYS.ACTIVE_DRAW_ID, id);
}

export function getActiveDrawId(): string | null {
  return safeRead(KEYS.ACTIVE_DRAW_ID);
}

export function clearActiveDrawId(): void {
  safeRemove(KEYS.ACTIVE_DRAW_ID);
}

export function resetWizardForNewDraw(): void {
  clearActiveDrawId();
  clearWizardSession();
}
